import {
  Box,
  BoxProps,
  Button,
  Stack,
  SvgIcon,
  Typography
} from "@mui/material";

import { Error20Px } from "../icons/autostoreIcons/icons20px";

type Props = {
  /**
   * The error message to display.
   * Defaults to "Oops, something went wrong. Please try again."
   * */
  message?: string;
  /** If provided, renders a button to retry a failure */
  tryAgain?: {
    /** Called when a user clicks the Try Again button */
    onTryAgain: () => void | Promise<void>;
    /** Overrides the default Try Again button text */
    text?: string;
  };
  /** Error panel variants */
  variant?: "filled" | "outline";
} & BoxProps;

/**
 * A panel that displays an error inline.
 *
 * Recommended usage includes any component that can render data in a rectangular area such
 * as a grid.
 */
export function ErrorPanel({
  message = "Oops, something went wrong. Please try again.",
  tryAgain,
  variant = "filled",
  ...rest
}: Props) {
  return (
    <Box
      minWidth="xl"
      minHeight="md"
      border="4px solid"
      borderRadius={3}
      borderColor="error.main"
      bgcolor={variant === "filled" ? "error.light" : "background.default"}
      px={6}
      py={3}
      {...rest}
    >
      <Stack alignItems="center" color="error.main" gap={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          gap={1}
        >
          <SvgIcon
            component={Error20Px}
            width={5}
            height={5}
            sx={{ alignSelf: "flex-end" }}
          />
          <Typography variant="subtitle1">Error</Typography>
        </Stack>
        <Typography variant="subtitle2" textAlign="center">
          {message}
        </Typography>
        {tryAgain && (
          <Button
            color="primary"
            onClick={tryAgain.onTryAgain}
            variant="contained"
          >
            {tryAgain.text || "Try Again"}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
